import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {of} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {AppAuthService} from './app-auth.service';
import {MatButtonModule} from '@angular/material/button';
import {MatExpansionModule} from '@angular/material/expansion';
import {AutotrimDirective} from '../directive/autotrim.directive';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {ClearFormStateDirective} from '../directive/clear-form-state.directive';
import {FormsModule} from '@angular/forms';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatCardModule} from '@angular/material/card';
import {MatDividerModule} from '@angular/material/divider';
import {MatIconModule} from '@angular/material/icon';
import {SocialAuthProviderService} from './social-auth-provider.service';

@Component({
  templateUrl: 'login.component.html',
  styleUrls: ['login.component.scss'],
  imports: [
    MatCardModule, MatToolbarModule, FormsModule, ClearFormStateDirective, MatFormFieldModule, MatInputModule,
    AutotrimDirective, MatExpansionModule, MatButtonModule, MatDividerModule, MatIconModule]
})
export class LoginComponent implements OnInit, OnDestroy {
  data = {login: '', password: ''};
  lastError;
  _subscription;
  waitPopup = false;

  constructor(private router: Router, private route: ActivatedRoute, private authService: AppAuthService,
              public providers: SocialAuthProviderService) {
  }

  login() {
    this.lastError = null;
    const back = this.route.snapshot.params.back;

    this.authService.login(this.data.login, this.data.password)
      .pipe(catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          this.lastError = err.error.error;
          return of();
        }
        throw err;
      }))
      .subscribe(() => this.router.navigateByUrl(back || '/'));
  }

  signInWith(id: string): void {
    this.waitPopup = true;
    this.providers.loadSocialLoginIds().then((config) => {
      const provider = config.providers.find(p => p.id === id);
      if (provider) {
        if (id === 'MICROSOFT') {
          this.signInWithMicrosoft(provider.provider.clientId);
        }
      } else {
        alert(`${id} provider not configured`);
        this.waitPopup = false;
      }
    }).catch(error => {
      console.error('Failed to load social auth providers:', error);
      alert('Social login configuration failed');
      this.waitPopup = false;
    });
  }


  private signInWithMicrosoft(clientId: string): void {
    const redirectUri = `${window.location.origin}/login`;
    const scope = 'openid profile email';
    const responseType = 'id_token';
    const nonce = Math.random().toString(36).substring(2, 15);

    const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
      `client_id=${encodeURIComponent(clientId)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=${encodeURIComponent(responseType)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `nonce=${encodeURIComponent(nonce)}`;

    this.openOAuthPopup(authUrl, 'MICROSOFT');
  }

  private openOAuthPopup(url: string, provider: string): void {
    const popup = window.open(url, 'oauth', 'width=500,height=600,scrollbars=yes');

    const checkClosed = setInterval(() => {
      if (popup?.closed) {
        clearInterval(checkClosed);
        this.waitPopup = false;
      }
    }, 1000);

    // Listen for messages from popup
    const messageListener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'OAUTH_SUCCESS') {
        clearInterval(checkClosed);
        popup?.close();
        this.waitPopup = false;
        window.removeEventListener('message', messageListener);

        this.handleOAuthToken(provider, event.data.token);
      } else if (event.data.type === 'OAUTH_ERROR') {
        clearInterval(checkClosed);
        popup?.close();
        this.waitPopup = false;
        window.removeEventListener('message', messageListener);

        this.lastError = 'OAuth authentication failed';
      }
    };

    window.addEventListener('message', messageListener);
  }

  private handleOAuthToken(provider: string, token: string): void {
    console.log(`${provider} OAuth token received`);
    this.authService.loginByToken(provider, token)
      .pipe(catchError(err => {
        if (err.status === 401) {
          this.lastError = 'Benutzer nicht gefunden oder inaktiv';
          return of(null);
        }
        this.lastError = 'Login fehlgeschlagen';
        throw err;
      }))
      .subscribe(result => {
        if (result) {
          this.router.navigate(['/']).then();
        }
      });
  }

  ngOnInit() {
    // Check if this is an OAuth redirect
    this.handleOAuthRedirect();

    // Load social auth configuration for display
    this.providers.loadSocialLoginIds().catch(error => {
      console.error('Failed to load social auth providers:', error);
      // Allow basic login to continue working
    });
  }

  private handleOAuthRedirect(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const fragment = new URLSearchParams(window.location.hash.substring(1));

    // Check for OAuth tokens in URL fragment or query params
    const idToken = fragment.get('id_token') || urlParams.get('id_token');
    const accessToken = fragment.get('access_token') || urlParams.get('access_token');
    const error = fragment.get('error') || urlParams.get('error');

    if (error) {
      // Send error back to parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'OAUTH_ERROR',
          error: error
        }, window.location.origin);
        window.close();
      } else {
        this.lastError = `OAuth error: ${error}`;
      }
      return;
    }

    if (idToken) {
      // Send token back to parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'OAUTH_SUCCESS',
          token: idToken
        }, window.location.origin);
        window.close();
      } else {
        // If not in popup, handle directly (for testing)
        console.log('OAuth redirect handled directly with token:', idToken);
      }
      return;
    }

    if (accessToken) {
      // Send access token back to parent window
      if (window.opener) {
        window.opener.postMessage({
          type: 'OAUTH_SUCCESS',
          token: accessToken
        }, window.location.origin);
        window.close();
      }
      return;
    }
  }

  ngOnDestroy() {
    if (this._subscription != null) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
  }
}
